import React, { useState } from 'react';
import { Modal, MenuItem, Radio, FormControlLabel, RadioGroup as MuiRadioGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  CloseIconButton,
  CancelButton,
  SendButton,
  ButtonRow,
} from './SmsModalStyled';
import {
  ChargeContentArea,
  ChargeScrollArea,
  ChargeFooter,
  ChargeInfoSection,
  ChargeInfoRow,
  ChargeInfoLabel,
  ChargeInfoValue,
  ChargeCountValue,
  ChargeCountUnit,
  ChargeSelectWrapper,
  ChargeSelect,
  ChargeNoticeBox,
  ChargeNoticeText,
  FieldGroup,
  FieldLabel,
  FieldInput,
  FieldSelect,
  RadioSection,
  RadioGroup,
  RadioGroupLabel,
  RadioOptionsRow,
  Divider,
  TotalRow,
  TotalLabel,
  TotalPrice,
  PrevButton,
  ConfirmSection,
  ConfirmRow,
  ConfirmLabel,
  ConfirmValue,
  ConfirmValueBold,
} from './SmsChargeModalStyled';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getSmsProductData, createSmsOrder } from '../../../../api/sms';

const PAYMENT_METHODS = [{ value: 'BANK_TRANSFER', label: '계좌이체' }];

const BANK_LIST = [
  '우리은행',
  '국민은행',
  '신한은행',
  '하나은행',
  '농협은행',
  '기업은행',
  '카카오뱅크',
  '토스뱅크',
  '케이뱅크',
];

const SmsChargeModal = ({ open, onClose, remainingCount = 0, balance }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const toast = useToast();

  const [step, setStep] = useState(1);

  // Step1
  const [selectedCharge, setSelectedCharge] = useState('');

  // Step2
  const [paymentMethod, setPaymentMethod] = useState('BANK_TRANSFER');
  const [depositorName, setDepositorName] = useState('');
  const [depositBank, setDepositBank] = useState('하나은행');
  const [receiptType, setReceiptType] = useState('신청안함');
  const [receiptCategory, setReceiptCategory] = useState('사업자 지출증빙');
  const [businessNumber, setBusinessNumber] = useState('');

  const { data: smsProductData = [] } = useQuery({
    queryKey: ['smsProductData'],
    queryFn: () => getSmsProductData(user?.token),
    enabled: !!user?.token,
  });

  const selectedProduct = smsProductData.find(p => p.id === selectedCharge);
  const priceText = selectedProduct ? `${selectedProduct.price.toLocaleString()}원` : '0원';

  const handleClose = () => {
    setStep(1);
    setSelectedCharge('');
    setPaymentMethod('BANK_TRANSFER');
    setDepositorName('');
    setDepositBank('하나은행');
    setReceiptType('신청안함');
    setReceiptCategory('사업자 지출증빙');
    setBusinessNumber('');
    onClose();
  };

  const handleStep1Next = () => {
    if (!selectedCharge) {
      toast.error(t('충전금액(건수)을 선택해주세요'));
      return;
    }
    setStep(2);
  };

  const handleStep2Next = () => {
    if (!paymentMethod) {
      toast.error(t('결제수단을 선택해주세요'));
      return;
    }
    if (!depositorName.trim()) {
      toast.error(t('입금자명을 입력해주세요'));
      return;
    }
    if (!depositBank) {
      toast.error(t('입금은행을 선택해주세요'));
      return;
    }
    setStep(3);
  };

  const orderMutation = useMutation({
    mutationFn: data => createSmsOrder(user?.branch?.branchId, data, user?.token),
    onSuccess: res => {
      toast.success(res?.message);
      handleClose();
    },
  });

  const handleComplete = () => {
    const taxInvoiceYn = receiptType === '현금영수증' && receiptCategory === '사업자 지출증빙' ? 'Y' : 'N';

    orderMutation.mutate({
      productId: selectedCharge,
      paymentMethod,
      depositorName,
      depositBank,
      taxInvoiceYn,
      businessNumber: receiptType === '현금영수증' ? businessNumber : '',
      taxInvoiceEmail: '',
      memo: '',
    });
  };

  const radioSx = {
    padding: 0,
    '&.Mui-checked': { color: '#4ab3bc' },
    '& .MuiSvgIcon-root': { fontSize: '24px' },
  };

  // Step 1: 충전금액 선택
  const renderStep1 = () => (
    <ChargeContentArea>
      <ChargeScrollArea>
        <ChargeInfoSection>
          <ChargeInfoRow>
            <ChargeInfoLabel>{t('업체명')}</ChargeInfoLabel>
            <ChargeInfoValue>{user?.headBranch?.headBranchName || '-'}</ChargeInfoValue>
          </ChargeInfoRow>
          <ChargeInfoRow>
            <ChargeInfoLabel>{t('지점명')}</ChargeInfoLabel>
            <ChargeInfoValue>{user?.branch?.branchName || '-'}</ChargeInfoValue>
          </ChargeInfoRow>
          <ChargeInfoRow>
            <ChargeInfoLabel>{t('잔여건수')}</ChargeInfoLabel>
            <ChargeCountValue>
              {balance}
              <ChargeCountUnit>{t('건')}</ChargeCountUnit>
            </ChargeCountValue>
          </ChargeInfoRow>
          <ChargeInfoRow>
            <ChargeInfoLabel>{t('충전금액(건수)')}</ChargeInfoLabel>
            <ChargeSelectWrapper>
              <ChargeSelect
                value={selectedCharge}
                onChange={e => setSelectedCharge(e.target.value)}
                displayEmpty
                IconComponent={KeyboardArrowDownIcon}
                renderValue={selected => {
                  if (!selected) return t('충전금액(건수) 선택');
                  const product = smsProductData.find(p => p.id === selected);
                  return product?.productName || '';
                }}>
                {smsProductData.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.productName} ({option.price.toLocaleString()}원)
                  </MenuItem>
                ))}
              </ChargeSelect>
            </ChargeSelectWrapper>
          </ChargeInfoRow>
          <div
            style={{
              marginTop: '4px',
              padding: '10px 12px',
              background: '#f7f8fa',
              borderRadius: '8px',
              fontSize: '12px',
              color: '#666',
              lineHeight: 1.6,
            }}>
            <div style={{ fontWeight: 600, color: '#333', marginBottom: '4px' }}>
              {t('발송 1건당 15원 · 문자 종류별 차감 건수')}
            </div>
            <div>{t('단문(SMS) · 1건 (15원)')}</div>
            <div>{t('장문(LMS) · 3건 (45원)')}</div>
            <div>{t('사진문자(MMS) · 6건 (90원)')}</div>
          </div>
          <ChargeNoticeBox>
            <ChargeNoticeText>
              {t('카드사 정책에 따라 현금충전성 상품인 SMS 충전시 계좌이체만 가능합니다.')}
            </ChargeNoticeText>
          </ChargeNoticeBox>
        </ChargeInfoSection>
      </ChargeScrollArea>
      <ChargeFooter>
        <CancelButton variant="contained" onClick={handleClose}>
          {t('취소')}
        </CancelButton>
        <SendButton variant="contained" onClick={handleStep1Next}>
          {t('다음')}
        </SendButton>
      </ChargeFooter>
    </ChargeContentArea>
  );

  // Step 2: 결제 정보 입력
  const renderStep2 = () => (
    <ChargeContentArea>
      <ChargeScrollArea>
        <ChargeInfoSection>
          <FieldGroup>
            <FieldLabel>{t('결제수단')}</FieldLabel>
            <FieldSelect
              value={paymentMethod}
              disabled
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              renderValue={() => t('계좌이체')}>
              <MenuItem value="BANK_TRANSFER">{t('계좌이체')}</MenuItem>
            </FieldSelect>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>{t('입금자명')}</FieldLabel>
            <FieldInput
              placeholder={t('입금자명을 입력해 주세요.')}
              value={depositorName}
              onChange={e => setDepositorName(e.target.value)}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>{t('입금은행')}</FieldLabel>
            <FieldSelect
              value={depositBank}
              disabled
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              renderValue={() => t('하나은행')}>
              <MenuItem value="하나은행">{t('하나은행')}</MenuItem>
            </FieldSelect>
          </FieldGroup>

          <RadioSection>
            <RadioGroup>
              <RadioGroupLabel>{t('세금계산서 발행')}</RadioGroupLabel>
              <RadioOptionsRow>
                <FormControlLabel
                  value="신청안함"
                  control={
                    <Radio
                      sx={radioSx}
                      checked={receiptType === '신청안함'}
                      onChange={() => setReceiptType('신청안함')}
                    />
                  }
                  label={t('신청안함')}
                  componentsProps={{ typography: { style: { fontSize: '14px', fontWeight: 500 } } }}
                />
                <FormControlLabel
                  value="현금영수증"
                  control={
                    <Radio
                      sx={radioSx}
                      checked={receiptType === '현금영수증'}
                      onChange={() => setReceiptType('현금영수증')}
                    />
                  }
                  label={t('현금영수증')}
                  componentsProps={{ typography: { style: { fontSize: '14px', fontWeight: 500 } } }}
                />
              </RadioOptionsRow>
            </RadioGroup>

            {receiptType === '현금영수증' && (
              <>
                <RadioGroup>
                  <RadioGroupLabel>{t('발행 구분')}</RadioGroupLabel>
                  <RadioOptionsRow>
                    <FormControlLabel
                      value="개인소득공제"
                      control={
                        <Radio
                          sx={radioSx}
                          checked={receiptCategory === '개인소득공제'}
                          onChange={() => setReceiptCategory('개인소득공제')}
                        />
                      }
                      label={t('개인소득공제')}
                      componentsProps={{ typography: { style: { fontSize: '14px', fontWeight: 500 } } }}
                    />
                    <FormControlLabel
                      value="사업자 지출증빙"
                      control={
                        <Radio
                          sx={radioSx}
                          checked={receiptCategory === '사업자 지출증빙'}
                          onChange={() => setReceiptCategory('사업자 지출증빙')}
                        />
                      }
                      label={t('사업자 지출증빙')}
                      componentsProps={{ typography: { style: { fontSize: '14px', fontWeight: 500 } } }}
                    />
                  </RadioOptionsRow>
                </RadioGroup>

                <FieldGroup style={{ gap: '8px' }}>
                  <RadioGroupLabel>
                    {receiptCategory === '사업자 지출증빙' ? t('사업자등록번호') : t('휴대폰번호')}
                  </RadioGroupLabel>
                  <FieldInput
                    placeholder={
                      receiptCategory === '사업자 지출증빙'
                        ? t('사업자등록번호를 입력해 주세요.')
                        : t('휴대폰번호를 입력해 주세요.')
                    }
                    value={businessNumber}
                    onChange={e => setBusinessNumber(e.target.value)}
                  />
                </FieldGroup>
              </>
            )}
          </RadioSection>

          <Divider />

          <TotalRow>
            <TotalLabel>{t('최종 결제 금액')}</TotalLabel>
            <TotalPrice>{priceText}</TotalPrice>
          </TotalRow>
        </ChargeInfoSection>
      </ChargeScrollArea>
      <ChargeFooter>
        <PrevButton variant="outlined" onClick={() => setStep(1)}>
          {t('이전')}
        </PrevButton>
        <SendButton variant="contained" onClick={handleStep2Next}>
          {t('다음')}
        </SendButton>
      </ChargeFooter>
    </ChargeContentArea>
  );

  // Step 3: 최종 확인
  const renderStep3 = () => (
    <ChargeContentArea>
      <ChargeScrollArea>
        <ConfirmSection>
          <ConfirmRow>
            <ConfirmLabel>{t('업체명')}</ConfirmLabel>
            <ConfirmValue>{user?.headBranch?.headBranchName || '-'}</ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>{t('지점명')}</ConfirmLabel>
            <ConfirmValue>{user?.branch?.branchName || '-'}</ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>{t('충전건수')}</ConfirmLabel>
            <ConfirmValue>
              {selectedProduct?.tokenCount?.toLocaleString() || 0} {t('건')}
            </ConfirmValue>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>{t('입금 계좌')}</ConfirmLabel>
            <ConfirmValueBold>하나은행 135-910030-53304 (㈜바디스위치)</ConfirmValueBold>
          </ConfirmRow>
          <ConfirmRow>
            <ConfirmLabel>{t('받는 계좌에 표시')}</ConfirmLabel>
            <ConfirmValue>{depositorName}</ConfirmValue>
          </ConfirmRow>
          <Divider />
          <TotalRow>
            <TotalLabel>{t('이체 금액')}</TotalLabel>
            <TotalPrice>{priceText}</TotalPrice>
          </TotalRow>
        </ConfirmSection>
      </ChargeScrollArea>
      <ChargeFooter>
        <PrevButton variant="outlined" onClick={() => setStep(2)}>
          {t('이전')}
        </PrevButton>
        <SendButton variant="contained" onClick={handleComplete} disabled={orderMutation.isPending}>
          {orderMutation.isPending ? t('처리중...') : t('완료')}
        </SendButton>
      </ChargeFooter>
    </ChargeContentArea>
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer style={{ width: '520px', maxHeight: '90vh' }}>
        <ModalHeader>
          <ModalHeaderTitle>{t('SMS 충전')}</ModalHeaderTitle>
          <CloseIconButton onClick={handleClose}>
            <CloseIcon style={{ fontSize: '24px' }} />
          </CloseIconButton>
        </ModalHeader>

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </ModalContainer>
    </Modal>
  );
};

export default SmsChargeModal;
